/**
 * The closed list a call's reasons for calling are sorted into, as a card of pills.
 *
 * WHAT A CATEGORY ACTUALLY IS, because a list of words does not say: it is what some number of
 * already-read calls are FILED UNDER, a line the analyser is shown on every call, and a bar on
 * the overview. That is why removing one asks where its calls go.
 *
 * EDITS ARE A DRAFT NOW, SAVED WITH THE PAGE'S ONE BAR. This file used to post every edit on
 * its own, and argued for it: a delete moves rows in the database, and pairing that with "also
 * rename the company" in one submit was never a transaction anybody meant. Settings v2 has one
 * bar, and Illia chose it (2026-09-26). What survives of the argument is the part that did the
 * work - the delete still asks where the calls go, before anything is marked, and the answer
 * travels as a `merges` entry in the same `POST /v1/settings/agent`, which the
 * Worker applies in one D1 batch. The pure edits are in `settings/draft.tsx`.
 *
 * KEYS ARE NEVER DERIVED HERE. The Worker slugs a label into the key every call is stored
 * under; a new row goes up with `key: null`, and the draft is re-read from the answer.
 *
 * A CATEGORY'S DESCRIPTION IS CARRIED, NEVER EDITED OR DROPPED. It is the one part the
 * ANALYSER reads, and the Worker treats a row sent without one as a row whose description was
 * deleted - which wipes it and offers a paid re-read. The draft rows carry it through.
 *
 * A CHIP CARRIES TWO ACTIONS AND IS THEREFORE NOT A BUTTON: a `<span>` holding the border, with
 * a rename button and a remove button inside it, because a button inside a button is invalid
 * HTML that browsers silently re-parent.
 */

import { useState } from "react";
import type { ReactNode } from "react";
import { Button, CountTag, Select, SettingCard, TextInput } from "../ui";
import { Dialog } from "./Dialog";
import { addCategory, removeCategory, renameCategory, type CategoryRow, type Values } from "./settings/draft";

/** The bucket every taxonomy has. The Worker knows it by this name too. */
const OTHER = "other";

/** The Worker's caps, so a control stops rather than being refused on save. */
const MAX_CATEGORIES = 30;
const MAX_LABEL_CHARS = 40;

const SAY = {
  title: "Reasons for calling",
  help: "Why people call. A call can have more than one; the main reason comes first. Up to 30.",
  one: "reason",
  add: "Add a reason for calling",
  prompt: "Add a reason",
  edit: "Edit a reason for calling",
  nameHint: "Shows up on “What callers wanted” and beside every call.",
  deleteBody: (label: string) => `Calls already sorted as “${label}” need somewhere to go.`,
};

/**
 * The two things that can be wrong with a name, said before anything is sent. The duplicate
 * check is on what is on screen; the Worker's is on slugs, so "New boat" and "new  boat" are
 * one category to it - being told here is a hint, being told on save is an error.
 */
function problem(label: string, rows: CategoryRow[], one: string, selfIndex?: number): string | null {
  const trimmed = label.trim();
  if (!trimmed) return `Give this ${one} a name`;
  const clash = rows.some((row, index) => index !== selfIndex && row.label.trim().toLowerCase() === trimmed.toLowerCase());
  return clash ? `“${trimmed}” is already on the list` : null;
}

export function Categories({
  taxonomy,
  saved,
  onChange,
}: {
  /** The draft. */
  taxonomy: Values["taxonomy"];
  /** What was last saved, so a re-added name can put its original back. */
  saved: Values["taxonomy"];
  onChange: (next: Values["taxonomy"]) => void;
}) {
  const say = SAY;
  const rows = taxonomy.intents;
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);
  const full = rows.length >= MAX_CATEGORIES;

  function add() {
    const wrong = problem(draft, rows, say.one);
    if (wrong) return setError(wrong);
    onChange(addCategory(taxonomy, saved, draft));
    setDraft("");
    setError(null);
  }

  return (
    <SettingCard className="grid gap-3.5 p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="grid gap-1">
          <h3 className="flex items-baseline gap-2 text-base font-semibold text-ink">
            {say.title}
            <CountTag n={rows.length} max={MAX_CATEGORIES} />
          </h3>
          <p className="max-w-[520px] text-xs text-ink-faint">{say.help}</p>
        </div>
        <div className="flex flex-none gap-1.5">
          <TextInput
            className="h-8.5 w-45 py-0"
            value={draft}
            disabled={full}
            maxLength={MAX_LABEL_CHARS}
            placeholder={say.prompt}
            aria-label={say.add}
            onChange={(event) => {
              setDraft(event.target.value);
              setError(null);
            }}
            onKeyDown={(event) => {
              if (event.key !== "Enter") return;
              event.preventDefault();
              add();
            }}
          />
          <Button type="button" variant="primary" className="h-8.5 py-0" disabled={full} onClick={add}>
            Add
          </Button>
        </div>
      </div>
      {error && <p className="text-xs text-alert">{error}</p>}
      {full && <p className="text-xs text-ink-faint">Thirty is the limit. A chart with more bars than that is one nobody reads.</p>}

      <ul className="flex flex-wrap gap-2">
        {rows.map((row, index) => (
          <li key={`${row.key ?? "new"}-${index}`}>
            <span className="inline-flex items-center gap-1 rounded-full border border-line bg-paper py-1.5 pr-1.5 pl-3.5 text-sm font-medium text-ink">
              <button type="button" className="cursor-pointer hover:text-accent" onClick={() => setEditing(index)}>
                {row.label}
              </button>
              <button
                type="button"
                aria-label={`Delete ${row.label}`}
                onClick={() => setDeleting(index)}
                className="grid size-5 flex-none cursor-pointer place-items-center rounded-full text-ink-ghost hover:bg-sunken hover:text-ink"
              >
                <span aria-hidden="true" className="text-sm leading-none">
                  ×
                </span>
              </button>
            </span>
          </li>
        ))}
        {/* THE BUCKET, AS THE LAST PILL. Every taxonomy has it and it is the biggest bar on
            the overview of a business whose list is too short, so it has to be visible here;
            the dashed border and the missing × say it is not one you wrote or can remove. */}
        <li>
          <span className="inline-flex items-center rounded-full border border-dashed border-line-strong px-3.5 py-1.5 text-sm text-ink-ghost">Other</span>
        </li>
      </ul>

      {editing !== null && rows[editing] && (
        <CategoryDialog
          say={say}
          row={rows[editing]!}
          check={(label) => problem(label, rows, say.one, editing)}
          onClose={() => setEditing(null)}
          onDelete={() => {
            setDeleting(editing);
            setEditing(null);
          }}
          onSave={(label) => {
            onChange(renameCategory(taxonomy, editing, label));
            setEditing(null);
          }}
        />
      )}
      {deleting !== null && rows[deleting] && (
        <DeleteDialog
          say={say}
          row={rows[deleting]!}
          // Only a SAVED row can take calls, so an unsaved one is not offered as a target.
          targets={rows.filter((row, index) => index !== deleting && row.key)}
          onClose={() => setDeleting(null)}
          onDelete={(into) => {
            onChange(removeCategory(taxonomy, deleting, into));
            setDeleting(null);
          }}
        />
      )}
    </SettingCard>
  );
}

function CategoryDialog({
  say,
  row,
  check,
  onClose,
  onDelete,
  onSave,
}: {
  say: typeof SAY;
  row: CategoryRow;
  check: (label: string) => string | null;
  onClose: () => void;
  onDelete: () => void;
  onSave: (label: string) => void;
}) {
  const [label, setLabel] = useState(row.label);
  const [error, setError] = useState<string | null>(null);
  const form = `category-${row.key ?? "new"}`;

  function submit(event: React.FormEvent) {
    event.preventDefault();
    const wrong = check(label);
    if (wrong) return setError(wrong);
    onSave(label.trim());
  }

  return (
    <Dialog
      title={say.edit}
      onClose={onClose}
      footer={
        <>
          <Button type="button" variant="danger" onClick={onDelete}>
            Delete
          </Button>
          <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
            {error && <span className="text-xs text-alert">{error}</span>}
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" form={form} variant="primary">
              Done
            </Button>
          </div>
        </>
      }
    >
      <form id={form} onSubmit={submit} className="grid gap-4">
        <Labelled label="Name" hint={`${say.nameHint} Saved with your other changes.`}>
          <TextInput
            className="w-full"
            autoFocus
            value={label}
            maxLength={MAX_LABEL_CHARS}
            onChange={(event) => {
              setLabel(event.target.value);
              setError(null);
            }}
          />
        </Labelled>
      </form>
    </Dialog>
  );
}

function Labelled({ label, hint, children }: { label: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <div>
      <label className="block">
        <span className="mb-1.5 block text-sm font-medium text-ink">{label}</span>
        {children}
      </label>
      {hint && <p className="mt-1.5 text-sm text-ink-faint">{hint}</p>}
    </div>
  );
}

/**
 * Where the calls go, asked before anything is marked. The Worker refuses a removal that
 * leaves filed calls with nowhere to go, so this is how the question is asked - and a name
 * added since the last save has no calls, so it goes without one.
 */
function DeleteDialog({
  say,
  row,
  targets,
  onClose,
  onDelete,
}: {
  say: typeof SAY;
  row: CategoryRow;
  targets: CategoryRow[];
  onClose: () => void;
  onDelete: (into: string) => void;
}) {
  const [into, setInto] = useState(OTHER);
  if (!row.key) {
    return (
      <Dialog
        title={`Remove “${row.label}”?`}
        onClose={onClose}
        footer={
          <div className="ml-auto flex gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="button" variant="danger" onClick={() => onDelete(OTHER)}>
              Remove {say.one}
            </Button>
          </div>
        }
      >
        <p className="text-sm text-ink-soft">It was added since the last save, so no calls are filed under it yet.</p>
      </Dialog>
    );
  }
  return (
    <Dialog
      title={`Delete “${row.label}”?`}
      onClose={onClose}
      footer={
        <div className="ml-auto flex gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" variant="danger" onClick={() => onDelete(into)}>
            Delete {say.one}
          </Button>
        </div>
      }
    >
      <p className="text-sm text-ink-soft">{say.deleteBody(row.label)} They move when you save your changes.</p>
      <label className="grid gap-1.5">
        <span className="text-sm font-medium text-ink">Move them to</span>
        <Select value={into} onChange={(event) => setInto(event.target.value)}>
          <option value={OTHER}>Other</option>
          {targets.map((other) => (
            <option key={other.key!} value={other.key!}>
              {other.label}
            </option>
          ))}
        </Select>
      </label>
    </Dialog>
  );
}
